import type { CredentialFlow, LocalSession } from '@/networks/types';
import type { AuthSession } from '@/types/auth';
import { normalizePublicSearchQuery } from '@/utils/publicSearchQuery';

export const SEARCH_CHAT_DRAFT_TTL = 300000;
const canonicalId =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export interface SearchDraftScope {
  authEpoch: number;
  guestEpoch: number;
  memberId?: string;
  accessToken?: string;
  guestToken?: string;
  locale: string;
  currency: string;
  expiresAt?: number;
  visible: boolean;
  online: boolean;
}
export interface AdoptedSearchDraft {
  text: string;
  deadline: number;
  binding: LocalSession;
  accessToken: string;
}
interface Draft {
  id: string;
  text: string;
  deadline: number;
  scope: SearchDraftScope;
  roomId?: string;
  roomSelectionPath?: string;
  credentialReturn?: string;
}
export function searchDraftId(path: string) {
  try {
    const url = new URL(path, 'https://glow.invalid');
    if (url.origin !== 'https://glow.invalid') return;
    const ids = url.searchParams.getAll('searchDraft');
    return ids.length === 1 && canonicalId.test(ids[0]) ? ids[0] : undefined;
  } catch {
    return;
  }
}
function sameScope(a: SearchDraftScope, b: SearchDraftScope) {
  return [
    'authEpoch',
    'guestEpoch',
    'memberId',
    'accessToken',
    'guestToken',
    'locale',
    'currency',
  ].every(
    (key) =>
      a[key as keyof SearchDraftScope] === b[key as keyof SearchDraftScope],
  );
}
function pathname(path: string) {
  try {
    const url = new URL(path, 'https://glow.invalid');
    return path.startsWith('/') &&
      !path.startsWith('//') &&
      url.origin === 'https://glow.invalid'
      ? url.pathname
      : '';
  } catch {
    return '';
  }
}
function eligiblePath(path: string) {
  const name = pathname(path);
  return (
    name === '/chat' ||
    /^\/chat\/[0-9a-f-]{36}$/.test(name) ||
    /^\/diagnosis\/[1-9][0-9]?$/.test(name) ||
    /^\/diagnosis\/[0-9a-f-]{36}\/result$/.test(name)
  );
}
/** Each instance lives inside one provider component; there is no module-global draft or storage. */
export class SearchChatDraftLease {
  #draft?: Draft;
  #credentialCommit = false;
  constructor(
    private clock: () => number = () => performance.now(),
    private wall: () => number = () => Date.now(),
  ) {}
  clear() {
    this.#draft = undefined;
  }
  #valid(scope: SearchDraftScope) {
    const draft = this.#draft;
    if (!draft) return;
    if (this.#credentialCommit) return draft;
    if (
      !scope.visible ||
      !scope.online ||
      !sameScope(draft.scope, scope) ||
      this.clock() >= draft.deadline ||
      (draft.scope.expiresAt !== undefined &&
        this.wall() >= draft.scope.expiresAt)
    ) {
      this.clear();
      return;
    }
    return draft;
  }
  #routeMatches(draft: Draft, path: string) {
    if (draft.credentialReturn === path) return true;
    const name = pathname(path);
    if (/^\/auth\/(sign-in|sign-up|reset-password)$/.test(name)) {
      if (draft.scope.memberId) return false;
      const values = new URL(path, 'https://glow.invalid').searchParams.getAll(
        'redirect',
      );
      return (
        values.length === 1 &&
        pathname(values[0]) === '/chat' &&
        searchDraftId(values[0]) === draft.id
      );
    }
    return (
      searchDraftId(path) === draft.id &&
      eligiblePath(path) &&
      (draft.roomId
        ? name === `/chat/${draft.roomId}` || path === draft.roomSelectionPath
        : name === '/chat' || name.startsWith('/diagnosis/'))
    );
  }
  info(scope: SearchDraftScope, path: string) {
    const draft = this.#valid(scope);
    if (!draft) return;
    if (!this.#credentialCommit && !this.#routeMatches(draft, path)) {
      this.clear();
      return;
    }
    if (eligiblePath(path)) draft.credentialReturn = undefined;
    if (draft.roomId && pathname(path) === `/chat/${draft.roomId}`)
      draft.roomSelectionPath = undefined;
    return { id: draft.id, deadline: draft.deadline };
  }
  start(text: string, scope: SearchDraftScope) {
    this.clear();
    if (
      !scope.visible ||
      !scope.online ||
      !!scope.accessToken !== !!scope.memberId
    )
      return;
    let q: string;
    try {
      q = normalizePublicSearchQuery(text);
    } catch {
      return;
    }
    const remaining =
      scope.expiresAt === undefined
        ? SEARCH_CHAT_DRAFT_TTL
        : Math.min(SEARCH_CHAT_DRAFT_TTL, scope.expiresAt - this.wall());
    if (remaining <= 0 || !Number.isFinite(remaining)) return;
    const id = crypto.randomUUID();
    this.#draft = {
      id,
      text: q,
      scope: { ...scope },
      deadline: this.clock() + remaining,
    };
    const target = `/chat?searchDraft=${id}`;
    return scope.memberId
      ? target
      : `/auth/sign-in?redirect=${encodeURIComponent(target)}`;
  }
  link(path: string, scope: SearchDraftScope, currentPath: string) {
    const info = this.info(scope, currentPath);
    if (!info || !eligiblePath(path)) return path;
    const url = new URL(path, 'https://glow.invalid');
    url.searchParams.set('searchDraft', info.id);
    return `${url.pathname}${url.search}${url.hash}`;
  }
  chooseRoom(roomId: string, scope: SearchDraftScope, currentPath: string) {
    const draft = this.#valid(scope);
    if (
      !draft ||
      !draft.scope.memberId ||
      !canonicalId.test(roomId) ||
      !this.info(scope, currentPath)
    )
      return false;
    if (draft.roomId && draft.roomId !== roomId) {
      this.clear();
      return false;
    }
    draft.roomId = roomId;
    draft.roomSelectionPath = currentPath;
    draft.credentialReturn = undefined;
    return true;
  }
  take(
    roomId: string,
    scope: SearchDraftScope,
    currentPath: string,
  ): AdoptedSearchDraft | undefined {
    const draft = this.#valid(scope);
    if (
      !draft ||
      !draft.scope.memberId ||
      !draft.scope.accessToken ||
      draft.roomId !== roomId ||
      !this.info(scope, currentPath)
    )
      return;
    const result = {
      text: draft.text,
      deadline: draft.deadline,
      binding: { actorId: draft.scope.memberId, epoch: draft.scope.authEpoch },
      accessToken: draft.scope.accessToken,
    };
    this.clear();
    return Object.freeze(result);
  }
  /** Only the successful, fenced credential callback may transfer an anonymous draft. */
  credentials(
    redirect: string,
    flow: CredentialFlow,
    expected: AuthSession,
    commit: () => void,
    scopeBefore: SearchDraftScope,
    currentPath: string,
    readScope: () => SearchDraftScope,
  ) {
    const draft = this.#valid(scopeBefore);
    const eligible =
      !!draft &&
      !draft.scope.memberId &&
      !draft.scope.accessToken &&
      searchDraftId(redirect) === draft.id &&
      pathname(redirect) === '/chat' &&
      this.#routeMatches(draft, currentPath) &&
      flow.authEpoch === scopeBefore.authEpoch &&
      flow.guestEpoch === scopeBefore.guestEpoch &&
      flow.locale === scopeBefore.locale &&
      (flow.currency === undefined || flow.currency === scopeBefore.currency);
    this.#credentialCommit = eligible;
    try {
      commit();
    } catch (cause) {
      this.clear();
      throw cause;
    } finally {
      this.#credentialCommit = false;
    }
    if (!eligible || !draft) {
      this.clear();
      return;
    }
    const next = readScope();
    if (
      !next.visible ||
      !next.online ||
      next.memberId !== expected.member.id ||
      next.accessToken !== expected.accessToken ||
      next.authEpoch !== scopeBefore.authEpoch + 1 ||
      next.guestEpoch !== scopeBefore.guestEpoch + 1 ||
      next.guestToken !== undefined ||
      next.locale !== scopeBefore.locale ||
      next.currency !== scopeBefore.currency ||
      !next.expiresAt ||
      next.expiresAt <= this.wall() ||
      this.clock() >= draft.deadline
    ) {
      this.clear();
      return;
    }
    draft.scope = { ...next };
    draft.deadline = Math.min(
      draft.deadline,
      this.clock() + next.expiresAt - this.wall(),
    );
    draft.credentialReturn = currentPath;
    this.#draft = draft;
  }
}
